import { OrcamentoIcon } from './OrcamentoUI';
import { useLiquidFeedback, LiquidDialog } from './LiquidUI';
import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import './WorkItemsManager.css';

const WorkItemsManager = ({ onClose, onUpdate }) => {
    const liquidFeedback = useLiquidFeedback();
    const [items, setItems] = useState([]);
    const [newItem, setNewItem] = useState({ categoria: 'Interior', nome: '', icone: '🔹' });
    const [loading, setLoading] = useState(false);
    const [editingItem, setEditingItem] = useState(null); // { id, categoria, nome, icone }
    const [showIconPicker, setShowIconPicker] = useState(false);
    const [showEditIconPicker, setShowEditIconPicker] = useState(false);

    useEffect(() => {
        fetchItems();
    }, []);

    const fetchItems = async () => {
        try {
            const res = await api.get('/config/trabalhos');
            setItems(res.data);
        } catch (err) {
            console.error('Erro ao carregar itens:', err);
        }
    };


    const handleImportExcel = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setLoading(true);
        try {
            const res = await api.post('/config/trabalhos/import', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            liquidFeedback.notify(`✅ Sucesso! Importados ${res.data.imported} itens.`);
            fetchItems();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error('Erro ao importar:', err);
            const status = err.response?.status;
            const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message;
            liquidFeedback.notify(`❌ Erro ao importar (${status || 'API'}): ${errorMsg}`);
        } finally {
            setLoading(false);
            e.target.value = null;
        }
    };

    const handleDownloadCatalogTemplate = async () => {
        try {
            const L = (await import('xlsx')).default;
            const wb = L.utils.book_new();

            // Format: Col A = Category (if Col B empty), Col B = Name.
            // Row 1: Headers (ignored by backend if keywords match)
            const ws_data = [
                ['Categoria', 'Trabalho'],
                ['INTERIOR', ''],
                ['', 'Exemplo Trabalho Interior 1'],
                ['', 'Exemplo Trabalho Interior 2'],
                ['EXTERIOR', ''],
                ['', 'Exemplo Trabalho Exterior 1']
            ];

            const ws = L.utils.aoa_to_sheet(ws_data);

            // Col widths
            ws['!cols'] = [{ wch: 20 }, { wch: 40 }];

            L.utils.book_append_sheet(wb, ws, "Catálogo");
            L.writeFile(wb, "template_catalogo_trabalhos.xlsx");
        } catch (err) {
            console.error('Erro ao gerar template:', err);
            liquidFeedback.notify('Erro ao gerar modelo Excel.');
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newItem.nome) return;

        setLoading(true);
        try {
            await api.post('/config/trabalhos', newItem);
            setNewItem({ ...newItem, nome: '' });
            fetchItems();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error('Erro ao criar item:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        if (e) e.preventDefault();
        if (!editingItem || !editingItem.nome) return;

        setLoading(true);
        try {
            console.log('UPDATING ITEM:', editingItem);
            const res = await api.put(`/config/trabalhos/${editingItem.id}`, editingItem);
            console.log('UPDATE SUCCESS:', res.data);
            setEditingItem(null);
            fetchItems();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error('Erro ao atualizar item:', err);
            const status = err.response?.status;
            const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message;
            liquidFeedback.notify(`❌ Erro ao atualizar item (${status || 'API'}): ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!await liquidFeedback.confirm('Tem a certeza que deseja eliminar este tipo de trabalho?')) return;

        try {
            await api.delete(`/config/trabalhos/${id}`);
            fetchItems();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error('Erro ao eliminar item:', err);
            const errorMsg = err.response?.data?.error || 'Erro ao eliminar item';
            liquidFeedback.notify(`❌ ${errorMsg}`);
        }
    };

    const COMMON_ICONS = ['🔹', '🔸', '🏠', '🏗️', '🧱', '🪵', '🔨', '🎨', '🌱', '💧', '⚡', '🔥', '🚪', '🪟', '🧹', '🪜'];

    return (
        <LiquidDialog onClose={onClose} className="work-items-overlay">
            <div className="work-items-modal">
                <div className="modal-header">
                    <h3>Gerir Tipos de Trabalho</h3>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={handleDownloadCatalogTemplate}
                            style={{ padding: '4px 10px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}
                        >
                            <OrcamentoIcon name="download" fallback="📥" /> Template Excel
                        </button>
                        <input
                            type="file"
                            id="import-work-catalog"
                            style={{ display: 'none' }}
                            accept=".xlsx, .xls, .csv"
                            onChange={handleImportExcel}
                        />
                        <button
                            className="btn-secondary"
                            style={{ padding: '4px 10px', fontSize: '12px' }}
                            onClick={() => document.getElementById('import-work-catalog').click()}
                        >
                            <OrcamentoIcon name="upload" fallback="📤" /> Importar catálogo
                        </button>
                        <button onClick={onClose} className="close-btn" aria-label="Fechar"><OrcamentoIcon name="close" fallback="×" /></button>
                    </div>
                </div>

                <div className="work-items-content">
                    <form onSubmit={handleCreate} className="add-item-form">
                        <select
                            value={newItem.categoria}
                            onChange={e => setNewItem({ ...newItem, categoria: e.target.value })}
                        >
                            <option value="Interior">Interior</option>
                            <option value="Exterior">Exterior</option>
                            <option value="Fachada">Fachada</option>
                            <option value="Estrutura">Estrutura</option>
                        </select>
                        <div className="icon-picker-container" style={{ position: 'relative' }}>
                            <button
                                type="button"
                                className="icon-select-btn"
                                onClick={() => setShowIconPicker(!showIconPicker)}
                            >
                                {newItem.icone}
                            </button>
                            {showIconPicker && (
                                <div className="icon-dropdown">
                                    {COMMON_ICONS.map(icon => (
                                        <span
                                            key={icon}
                                            onClick={() => {
                                                setNewItem({ ...newItem, icone: icon });
                                                setShowIconPicker(false);
                                            }}
                                        >
                                            {icon}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <input
                            type="text"
                            value={newItem.nome}
                            onChange={e => setNewItem({ ...newItem, nome: e.target.value })}
                            placeholder="Nome do trabalho..."
                            className="name-input"
                        />
                        <button type="submit" disabled={loading}>+</button>
                    </form>

                    <div className="items-list-container">
                        {[...new Set(items.map(i => i.categoria))].map(cat => (
                            <div key={cat} className="category-group">
                                <h4>{cat}</h4>
                                <ul>
                                    {items.filter(i => i.categoria === cat).map(item => (
                                        <li key={item.id}>
                                            {editingItem && editingItem.id === item.id ? (
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                                                    <div style={{ position: 'relative' }}>
                                                        <button
                                                            type="button"
                                                            className="icon-select-btn"
                                                            onClick={() => setShowEditIconPicker(!showEditIconPicker)}
                                                        >
                                                            {editingItem.icone}
                                                        </button>
                                                        {showEditIconPicker && (
                                                            <div className="icon-dropdown">
                                                                {COMMON_ICONS.map(icon => (
                                                                    <span key={icon} onClick={() => {
                                                                        setEditingItem({ ...editingItem, icone: icon });
                                                                        setShowEditIconPicker(false);
                                                                    }}>{icon}</span>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <input
                                                        type="text"
                                                        value={editingItem.nome}
                                                        onChange={e => setEditingItem({ ...editingItem, nome: e.target.value })}
                                                        className="name-input"
                                                        style={{ height: '38px' }}
                                                    />
                                                    <button aria-label="Guardar" onClick={handleUpdate} className="btn-save"><OrcamentoIcon name="save" fallback="💾" /></button>
                                                    <button aria-label="Fechar" onClick={() => setEditingItem(null)} className="btn-cancel"><OrcamentoIcon name="close" fallback="❌" /></button>
                                                </div>
                                            ) : (
                                                <>
                                                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                                        <span className="item-icon">{item.icone}</span>
                                                        <span className="item-name">{item.nome}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '5px' }}>
                                                        <button
                                                            className="edit-item-btn"
                                                            onClick={() => setEditingItem(item)}
                                                            title="Editar"
                                                        >
                                                            <OrcamentoIcon name="edit" fallback="✏️" />
                                                        </button>
                                                        <button
                                                            className="delete-item-btn"
                                                            onClick={() => handleDelete(item.id)}
                                                            title="Eliminar"
                                                        >
                                                            <OrcamentoIcon name="trash" fallback="🗑️" />
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </LiquidDialog>
    );
};

export default WorkItemsManager;
